import {
  InfiniteData,
  UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { API } from 'api';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { OrganizationMember } from 'features/users/users.types';

type UseOrganizationMembersProps = {
  enabled?: boolean;
  query?: string;
};

type UseOrganizationMembers = Omit<
  UseInfiniteQueryResult<
    InfiniteData<AxiosResponse<MembersPaginatedData>, unknown>,
    Error
  >,
  'data'
> & {
  members: OrganizationMember[];
  totalCount: number;
};

type MembersPaginatedData = {
  count: number;
  results: OrganizationMember[];
  next: string | null;
  previous: string | null;
};

const PAGE_SIZE = 50;

const fetchOrganizationMembers = async ({
  limit,
  offset,
  query,
}: {
  limit: number;
  offset: number;
  query?: string;
}): Promise<AxiosResponse<MembersPaginatedData>> => {
  const params: AxiosRequestConfig<MembersPaginatedData>['params'] = query
    ? { limit, offset, q: query }
    : { limit, offset };
  return API.get<MembersPaginatedData>('organization/members/', { params });
};

export const useOrganizationMembers = (
  props: UseOrganizationMembersProps = {},
): UseOrganizationMembers => {
  const { enabled = true, query } = props;

  const { data, ...results } = useInfiniteQuery({
    queryKey: ['organizationMembers', query],
    queryFn: ({ pageParam }) =>
      fetchOrganizationMembers({ limit: PAGE_SIZE, offset: pageParam, query }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.data.next ? lastPageParam + PAGE_SIZE : null;
    },
    enabled,
  });

  const members = (
    data?.pages.flatMap((page) => page.data.results) ?? []
  ).filter(Boolean) as OrganizationMember[];

  const totalCount = data?.pages[0]?.data.count || 0;

  return { ...results, members, totalCount };
};
